import { DATA_TOOL_LIMITS, normalizeText, validateTextSource } from "./data-tool-core.js";

export function formatXml(source, { indentation = 2, compact = false } = {}) {
  const text = normalizeText(source);
  const validation = validateTextSource(text, "XML");
  if (validation) return { error: validation, output: "" };
  if (/<!DOCTYPE/i.test(text)) return { error: "DOCTYPE declarations are not supported.", output: "" };
  try {
    const root = parseXml(text);
    const unit = Number(indentation) === 4 ? "    " : "\t" === indentation ? "\t" : "  ";
    const output = serializeNode(root, { compact, unit }).trim();
    if (output.length > DATA_TOOL_LIMITS.outputCharacters)
      return { error: "Generated output exceeds the 8,000,000 character limit.", output: "" };
    return { error: "", output, lines: output ? output.split("\n").length : 0 };
  } catch (error) {
    return { error: error?.message || "Invalid XML.", output: "" };
  }
}

function parseXml(source) {
  const document = { type: "document", children: [] };
  const stack = [document];
  let position = 0;
  let rootElements = 0;
  const add = node => stack.at(-1).children.push(node);

  while (position < source.length) {
    if (source[position] !== "<") {
      const end = source.indexOf("<", position);
      const value = source.slice(position, end < 0 ? source.length : end);
      if (stack.length === 1 && value.trim()) throw xmlError(source, position, "Text is not allowed outside the document element");
      validateEntities(value, source, position);
      add({ type: "text", value });
      position = end < 0 ? source.length : end;
      continue;
    }
    if (source.startsWith("<!--", position)) {
      const end = source.indexOf("-->", position + 4);
      if (end < 0) throw xmlError(source, position, "Unclosed XML comment");
      const value = source.slice(position, end + 3);
      if (value.slice(4, -3).includes("--")) throw xmlError(source, position, "XML comments cannot contain --");
      add({ type: "raw", value });
      position = end + 3;
      continue;
    }
    if (source.startsWith("<![CDATA[", position)) {
      if (stack.length === 1) throw xmlError(source, position, "CDATA is not allowed outside the document element");
      const end = source.indexOf("]]>", position + 9);
      if (end < 0) throw xmlError(source, position, "Unclosed CDATA section");
      add({ type: "raw", value: source.slice(position, end + 3) });
      position = end + 3;
      continue;
    }
    if (source.startsWith("<?", position)) {
      const end = source.indexOf("?>", position + 2);
      if (end < 0) throw xmlError(source, position, "Unclosed processing instruction");
      add({ type: "raw", value: source.slice(position, end + 2) });
      position = end + 2;
      continue;
    }
    const end = findTagEnd(source, position);
    const raw = source.slice(position, end + 1);
    if (raw.startsWith("</")) {
      const name = raw.slice(2, -1).trim();
      if (!XML_NAME.test(name)) throw xmlError(source, position, "Invalid closing element name");
      if (stack.length === 1 || stack.at(-1).name !== name)
        throw xmlError(source, position, `Unexpected closing element </${name}>`);
      stack.pop();
    } else {
      const selfClosing = /\/\s*>$/.test(raw);
      const inside = raw.slice(1, selfClosing ? raw.lastIndexOf("/") : -1).trim();
      const name = inside.match(/^([^\s/>]+)/)?.[1] || "";
      if (!XML_NAME.test(name)) throw xmlError(source, position, "Invalid element name");
      validateAttributes(inside.slice(name.length), source, position);
      const node = { type: "element", name, open: raw, children: [] };
      add(node);
      if (stack.length === 1) {
        rootElements += 1;
        if (rootElements > 1) throw xmlError(source, position, "XML can contain only one document element");
      }
      if (!selfClosing) stack.push(node);
    }
    position = end + 1;
  }
  if (stack.length > 1) throw new Error(`Unclosed element <${stack.at(-1).name}>.`);
  if (rootElements !== 1) throw new Error("XML needs one document element.");
  return document;
}

const XML_NAME = /^[A-Za-z_:][A-Za-z0-9_.:-]*$/;

function findTagEnd(source, start) {
  let quote = "";
  for (let index = start + 1; index < source.length; index += 1) {
    const character = source[index];
    if (quote) {
      if (character === quote) quote = "";
    } else if (character === "\"" || character === "'") quote = character;
    else if (character === ">") return index;
  }
  throw xmlError(source, start, "Unclosed XML tag");
}

function validateAttributes(source, fullSource, position) {
  let rest = source.trim();
  const names = new Set();
  while (rest) {
    const match = rest.match(/^([A-Za-z_:][A-Za-z0-9_.:-]*)\s*=\s*("[^"]*"|'[^']*')/);
    if (!match) throw xmlError(fullSource, position, "Invalid or unquoted XML attribute");
    if (names.has(match[1])) throw xmlError(fullSource, position, `Duplicate attribute "${match[1]}"`);
    names.add(match[1]);
    validateEntities(match[2].slice(1, -1), fullSource, position);
    rest = rest.slice(match[0].length).trim();
  }
}

function validateEntities(value, source, position) {
  const invalid = value.match(/&(?!(?:amp|lt|gt|apos|quot|#\d+|#x[0-9a-fA-F]+);)/);
  if (invalid) throw xmlError(source, position + invalid.index, "Invalid XML entity");
}

function serializeNode(node, options, depth = 0) {
  if (node.type === "text") return node.value.trim() ? node.value : "";
  if (node.type === "raw") return node.value;
  const children = node.children.filter(child => child.type !== "text" || child.value.trim());
  if (node.type === "document") {
    return children.map(child => serializeNode(child, options, 0)).join(options.compact ? "" : "\n");
  }
  if (/\/\s*>$/.test(node.open)) return node.open;
  const closing = `</${node.name}>`;
  if (!children.length) return `${node.open}${closing}`;
  const inline = children.every(child => child.type === "text" || child.type === "raw" && child.value.startsWith("<![CDATA["));
  if (inline || options.compact) return `${node.open}${children.map(child => serializeNode(child, options, depth + 1)).join("")}${closing}`;
  const inner = children.map(child => `${options.unit.repeat(depth + 1)}${serializeNode(child, options, depth + 1)}`).join("\n");
  return `${node.open}\n${inner}\n${options.unit.repeat(depth)}${closing}`;
}

function xmlError(source, position, message) {
  const before = source.slice(0, position);
  const line = before.split("\n").length;
  const column = position - before.lastIndexOf("\n");
  return new Error(`${message} near line ${line}, column ${column}.`);
}
